import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Actions Redux
import {
  fetchMouvementsOr,
  deleteMouvementOr,
  createMouvementOr,
  updateMouvementOr,
  getListeFournisseur,
  getCours,
  getListeClient,
  fetchMouvementsOrClient,
  createMouvementOrClient,
  updateMouvementOrClient,
  deleteMouvementOrClient,
  getMetaux,
  getListeMetal,
  fetchMouvementsClal,
  createMouvementClal,
  updateMouvementClal,
  deleteMouvementClal
} from '../../actions';

// Sélecteurs optimisés
import { mapStateToPropsSelectors } from './MouvementsOrList/selectors';

// Composants modulaires
// IMPORTANT: utilisation explicite de l'index du dossier pour éviter le conflit
// avec ce fichier de même nom (sinon Webpack résout le fichier avant le dossier)
import {
  FiltersBar,
  MovementsTable,
  PaginationControls,
  DeleteDialog
} from './MouvementsOrList/index';

// Hooks personnalisés
import {
  useFilters,
  usePagination,
  useMouvementsData,
  useFormSubmit
} from './MouvementsOrList/index';

// Utilitaires et constantes
import { TAB_INDICES, SOURCE_TYPES, DEFAULT_ROWS_PER_PAGE } from './MouvementsOrList/constants';
import { handleSimplePrint, handlePoliceBookPrint } from '../../utils/printing';
import { formatCents } from '../../helpers/precision';

// Formulaires
import GoldForm from '../../components/forms/GoldForm';
import ClalForm from '../../components/forms/ClalForm';

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      overflowY: 'scroll',
      scrollbarGutter: 'stable'
    }
  },
  wrap: { 
    maxWidth: 1700, 
    margin: '0 auto', 
    padding: theme.spacing(0, 3, 6) 
  },
  formAndListLayout: { 
    display: 'grid', 
    gridTemplateColumns: '420px 1fr', 
    gap: theme.spacing(3)
  },
  listColumnWrap: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 480,
    overflow: 'hidden',
    boxShadow: '0 10px 26px -6px rgba(20,30,55,0.18), 0 4px 12px -3px rgba(20,30,55,0.10)',
    borderRadius: 20,
    background: '#fff'
  },
  listScrollable: { 
    flex: 1, 
    overflow: 'hidden', 
    paddingRight: 4, 
    position: 'relative', 
    display: 'flex', 
    flexDirection: 'column' 
  }
}));

/**
 * Composant principal orchestrateur pour la gestion des mouvements d'or
 */
const MouvementsOrList = ({
  // Props Redux
  rawMovementsData,
  metauxList,
  filtFournisseurs,
  filtClients,
  perteCoursClient,
  coursVieilOr,
  // Actions Redux
  fetchMouvementsOr,
  deleteMouvementOr,
  createMouvementOr,
  updateMouvementOr,
  fetchMouvementsOrClient,
  deleteMouvementOrClient,
  createMouvementOrClient,
  updateMouvementOrClient,
  getListeFournisseur,
  getMetaux,
  getListeMetal,
  getCours,
  getListeClient,
  fetchMouvementsClal,
  createMouvementClal,
  updateMouvementClal,
  deleteMouvementClal
}) => {
  const classes = useStyles();

  // États locaux
  const [tab, setTab] = useState(TAB_INDICES.CLAL);
  const [editRow, setEditRow] = useState(null);
  const [editClalRow, setEditClalRow] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [returnAfterEdit, setReturnAfterEdit] = useState(null);
  const [lastEditedRowId, setLastEditedRowId] = useState(null);
  const [selectedMetal, setSelectedMetal] = useState(null);
  const [bodyScrollHeight, setBodyScrollHeight] = useState(null);
  const [listDynamicHeight, setListDynamicHeight] = useState(null);

  // Refs
  const listWrapRef = useRef(null);
  const filtersRef = useRef(null);
  const headerBlockRef = useRef(null);
  const didInitialFetch = useRef(false);

  // Dates par défaut
  const yearNow = new Date().getFullYear();
  const firstJan = `${yearNow}-01-01`;
  const lastDec = `${yearNow}-12-31`;

  // Hook de filtres
  const {
    filters,
    setFilters,
    tempDateDebut,
    setTempDateDebut,
    tempDateFin,
    setTempDateFin,
    handleDateDebutBlur,
    handleDateFinBlur,
    handleDateDebutKeyDown,
    handleDateFinKeyDown,
    resetFilters
  } = useFilters(firstJan, lastDec);

  // Données préparées pour l'onglet actuel (supprimé si non utilisé)
  // const currentTabData = useMemo(() => {
  //   return prepareTabData(rawMovementsData, tab);
  // }, [rawMovementsData, tab]);

  // Calcul des données progressives
  const progressiveByMetal = useMouvementsData(rawMovementsData, tab, filters);

  // Hook de pagination
  const {
    // page: clalPage, // non utilisé
    itemsPerPage: clalRowsPerPage,
    metrics: paginationMetrics,
    safeSetPage: safeSetClalPage,
    safeSetItemsPerPage: safeSetClalRowsPerPage,
    resetPage,
    paginateData
  } = usePagination(
    selectedMetal && progressiveByMetal.displayGroups[selectedMetal] 
      ? progressiveByMetal.displayGroups[selectedMetal].length 
      : 0,
    DEFAULT_ROWS_PER_PAGE
  );

  // Hook de soumission
  const { handleSubmit } = useFormSubmit({
    tab,
    fetchMouvementsOr,
    fetchMouvementsOrClient,
    createMouvementOr,
    updateMouvementOr,
    createMouvementOrClient,
    updateMouvementOrClient,
    setEditRow,
    setLastEditedRowId,
    returnAfterEdit,
    setReturnAfterEdit,
    setTab
  });

  // Données paginées du métal sélectionné
  const paginatedMetalData = useMemo(() => {
    if (!selectedMetal || !progressiveByMetal.displayGroups[selectedMetal]) {
      return [];
    }
    
    const metalRows = progressiveByMetal.displayGroups[selectedMetal];
    
    if (tab === TAB_INDICES.CLAL && metalRows.length > clalRowsPerPage) {
      return paginateData(metalRows);
    }
    
    return metalRows;
  }, [selectedMetal, progressiveByMetal.displayGroups, tab, paginateData, clalRowsPerPage]);

  // Total prix d'achat (visible uniquement quand le filtre achat est actif sur l'onglet CLAL)
  const totalPrixAchat = useMemo(() => {
    if (tab !== TAB_INDICES.CLAL || filters.achat !== '1') return null;
    let total = 0;
    Object.values(progressiveByMetal.displayGroups).forEach(rows => {
      rows.forEach(row => {
        const pa = parseFloat(row.prix_achat || 0);
        if (!isNaN(pa)) total += pa;
      });
    });
    return total;
  }, [tab, filters.achat, progressiveByMetal.displayGroups]);

  // --- EFFETS ---

  // Initialisation des données
  useEffect(() => {
    if (!didInitialFetch.current) {
      fetchMouvementsOr();
      fetchMouvementsOrClient();
      if (typeof fetchMouvementsClal === 'function') fetchMouvementsClal();
      if (typeof getListeFournisseur === 'function') getListeFournisseur();
      if (typeof getListeClient === 'function') getListeClient();
      if (typeof getMetaux === 'function') getMetaux();
      if (typeof getListeMetal === 'function') getListeMetal();
      if (typeof getCours === 'function') getCours();
      didInitialFetch.current = true;
    }
  }, [
    fetchMouvementsOr,
    fetchMouvementsOrClient,
    fetchMouvementsClal,
    getListeFournisseur,
    getListeClient,
    getCours,
    getMetaux,
    getListeMetal
  ]);

  // Calcul dynamique de la hauteur
  useEffect(() => {
    const recompute = () => {
      if (!listWrapRef.current) return;
      const rect = listWrapRef.current.getBoundingClientRect();
      const WRAP_BOTTOM_PADDING = 48;
      const EXTRA_MARGIN = 30;
      const SAFETY = 12;
      const BOTTOM_RESERVED = WRAP_BOTTOM_PADDING + EXTRA_MARGIN + SAFETY;
      let available = window.innerHeight - rect.top - BOTTOM_RESERVED;
      const MAX_HEIGHT = 980;
      if (available > MAX_HEIGHT) available = MAX_HEIGHT;
      if (available < 360) available = 360;
      setListDynamicHeight(available);

      requestAnimationFrame(() => {
        if (!listWrapRef.current) return;
        const filtersH = filtersRef.current?.getBoundingClientRect?.()?.height || 0;
        const tableAvailable = available - filtersH - 12;
        const headerH = headerBlockRef.current?.getBoundingClientRect?.()?.height || 0;
        const bodyH = tableAvailable - headerH;
        setBodyScrollHeight(bodyH > 120 ? bodyH : 120);
      });
    };
    
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);

  // Focus automatique sur le champ date
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const dateInput = document.querySelector('input[name="date"]');
        if (dateInput && typeof dateInput.focus === 'function') dateInput.focus();
      } catch (_) { /* ignore */ }
    }, 80);
    return () => clearTimeout(t);
  }, [tab]);

  // Réinitialiser la pagination lors du changement de filtres
  useEffect(() => {
    resetPage();
  }, [filters.dateDebut, filters.dateFin, filters.fournisseurId, filters.clientId, filters.metalId, filters.designation, filters.achat, resetPage]);

  // Assurer la sélection d'un métal existant
  useEffect(() => {
    const metalIds = Object.keys(progressiveByMetal.displayGroups);
    if (!metalIds.length) {
      setSelectedMetal(null);
      return;
    }
    if (!selectedMetal || !progressiveByMetal.displayGroups[selectedMetal]) {
      const preferred = metalIds.includes('1') ? '1' : metalIds[0];
      setSelectedMetal(preferred);
    }
  }, [progressiveByMetal.displayGroups, selectedMetal]);

  // --- GESTIONNAIRES D'ÉVÉNEMENTS ---

  const handleTabChange = useCallback((e, v) => {
    setTab(v);
    setEditRow(null);
  }, []);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(f => ({ ...f, [field]: value }));
  }, [setFilters]);

  const handleResetFilters = useCallback(() => {
    resetFilters(firstJan);
  }, [resetFilters, firstJan]);

  const handleEdit = useCallback((row) => {
    setEditRow(row);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDelete = useCallback((row) => {
    if (row.__source === SOURCE_TYPES.CLAL) {
      setDeleteDialog({ id: row.id, type: 'clal' });
    } else if (row.__source === SOURCE_TYPES.FOURNISSEUR) {
      setDeleteDialog({ id: row.id, type: 'f' });
    } else if (row.__source === SOURCE_TYPES.CLIENT) {
      setDeleteDialog({ id: row.id, type: 'c' });
    }
  }, []);

  const confirmDeletion = useCallback(async () => {
    if (!deleteDialog) return;
    
    try {
      if (deleteDialog.type === 'f') {
        await deleteMouvementOr(deleteDialog.id);
        await fetchMouvementsOr();
      } else if (deleteDialog.type === 'c') {
        await deleteMouvementOrClient(deleteDialog.id);
        await fetchMouvementsOrClient();
      } else if (deleteDialog.type === 'clal') {
        await deleteMouvementClal(deleteDialog.id);
        if (typeof fetchMouvementsClal === 'function') await fetchMouvementsClal();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteDialog(null);
    }
  }, [deleteDialog, deleteMouvementOr, deleteMouvementOrClient, deleteMouvementClal, fetchMouvementsOr, fetchMouvementsOrClient, fetchMouvementsClal]);

  const editFromAggregated = useCallback((row) => {
    if (row.__source === SOURCE_TYPES.CLAL) {
      setEditClalRow(row);
      setEditRow(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (row.__source === SOURCE_TYPES.FOURNISSEUR) {
      // Si on est déjà sur l'onglet fournisseurs, pas besoin de changer d'onglet ni de retourner
      if (tab === TAB_INDICES.FOURNISSEURS) {
        setReturnAfterEdit(null);
      } else {
        setReturnAfterEdit(TAB_INDICES.CLAL);
        setTab(TAB_INDICES.FOURNISSEURS);
      }
      setTimeout(() => {
        // Calcul de sortieOrFin avec la perte pour affichage
        const perteNum = parseFloat(row.perte) || 0;
        const sortieNum = parseFloat(row.sortie_or_750) || 0;
        const sortieFinBase = sortieNum * 0.75;
        const perteGrammes = sortieFinBase * (perteNum / 100);
        const sortieOrFin = sortieFinBase + perteGrammes;
        handleEdit({
          id: row.id,
          date: row.date_mouvement,
          fournisseur: filtFournisseurs.find(f => String(f.id) === String(row.fournisseur_id)),
          perte: row.perte,
          designation: row.designation,
          entreeOrFin: row.entree_or_fin,
          sortieOr750: row.sortie_or_750,
          sortieOrFin: sortieOrFin ? sortieOrFin.toFixed(2) : '',
          metal: row.metal_id
        });
      }, 0);
    } else if (row.__source === SOURCE_TYPES.CLIENT) {
      // Si on est déjà sur l'onglet clients, pas besoin de changer d'onglet ni de retourner
      if (tab === TAB_INDICES.CLIENTS) {
        setReturnAfterEdit(null);
      } else {
        setReturnAfterEdit(TAB_INDICES.CLAL);
        setTab(TAB_INDICES.CLIENTS);
      }
      setTimeout(() => {
        handleEdit({
          id: row.id,
          date: row.date_mouvement,
          fournisseur: filtClients.find(c => String(c.id) === String(row.client_id)),
          perte: row.perte,
          designation: row.designation,
          entreeOrFin: row.entree_or_fin,
          sortieOr750: row.sortie_or_fin,
          metal: row.metal_id
        });
      }, 0);
    }
  }, [filtFournisseurs, filtClients, handleEdit, tab]);

  const handlePrint = useCallback(() => {
    handleSimplePrint(selectedMetal, progressiveByMetal, tab, metauxList, filters, formatCents);
  }, [selectedMetal, progressiveByMetal, tab, metauxList, filters]);

  const handlePrintPoliceBook = useCallback(() => {
    handlePoliceBookPrint(progressiveByMetal, metauxList, tab, filters, formatCents);
  }, [progressiveByMetal, metauxList, tab, filters]);

  const handleClalFormAdd = useCallback(async (row, isEditing) => {
    const payload = {
      date: row.date_mouvement,
      designation: row.designation,
      note: row.note || '',
      metal_id: row.metal_id,
      entree_or_fin: row.entree_or_fin,
      sortie_or_750: row.sortie_or_750,
      sortie_or_fin: row.sortie_or_fin,
      perte: 0,
      achat: row.achat ? 1 : 0,
      prix_achat: row.prix_achat
    };
    
    try {
      if (isEditing) {
        await updateMouvementClal(row.id, payload);
        setEditClalRow(null);
      } else {
        await createMouvementClal(payload);
      }
      if (typeof fetchMouvementsClal === 'function') await fetchMouvementsClal();
    } catch (e) {
      throw e;
    }
  }, [updateMouvementClal, createMouvementClal, fetchMouvementsClal]);

  // --- RENDU ---

  return (
    <div className={classes.wrap}>
      <FiltersBar
        tab={tab}
        onTabChange={handleTabChange}
        filters={filters}
        tempDateDebut={tempDateDebut}
        tempDateFin={tempDateFin}
        onTempDateDebutChange={e => setTempDateDebut(e.target?.value || '')}
        onTempDateFinChange={e => setTempDateFin(e.target?.value || '')}
        onDateDebutBlur={handleDateDebutBlur}
        onDateFinBlur={handleDateFinBlur}
        onDateDebutKeyDown={handleDateDebutKeyDown}
        onDateFinKeyDown={handleDateFinKeyDown}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        fournisseurs={filtFournisseurs}
        clients={filtClients}
        metaux={metauxList}
        filtersRef={filtersRef}
        totalPrixAchat={totalPrixAchat}
      />

      <div className={classes.formAndListLayout}>
        {/* Formulaire */}
        <div>
          {tab === TAB_INDICES.CLAL ? (
            <ClalForm
              metaux={metauxList}
              editing={!!editClalRow}
              initialValuesData={editClalRow ? {
                id: editClalRow.id,
                date: editClalRow.date_mouvement,
                designation: editClalRow.designation,
                note: editClalRow.note || '',
                metal: editClalRow.metal_id,
                entreeFin: editClalRow.entree_or_fin != null ? editClalRow.entree_or_fin : (editClalRow.entree_fin || ''),
                sortie750: editClalRow.sortie_or_750 || '',
                sortieFin: editClalRow.sortie_or_fin != null ? editClalRow.sortie_or_fin : (editClalRow.sortie_fin || ''),
                achat: !!editClalRow.achat,
                prixAchat: editClalRow.prix_achat || ''
              } : null}
              onCancelEdit={() => setEditClalRow(null)}
              onAdd={handleClalFormAdd}
            />
          ) : (
            <GoldForm
              title={tab === TAB_INDICES.FOURNISSEURS ? 'Fournisseur' : 'Client'}
              fournisseurs={tab === TAB_INDICES.FOURNISSEURS ? filtFournisseurs : filtClients}
              metaux={metauxList}
              onSubmit={(values) => handleSubmit(values, editRow)}
              editing={!!editRow}
              initialValuesData={editRow}
              onCancelEdit={() => setEditRow(null)}
              compact
              entityLabel={tab === TAB_INDICES.FOURNISSEURS ? 'Fournisseur' : 'Client'}
              perteReadOnly={tab === TAB_INDICES.CLIENTS}
              autoPerteValue={tab === TAB_INDICES.CLIENTS ? perteCoursClient : null}
              sortieLabel={tab === TAB_INDICES.FOURNISSEURS ? 'Sortie Or 750' : 'Sortie Or Fin'}
              isClient={tab === TAB_INDICES.CLIENTS}
              vieilOrCoursValue={coursVieilOr}
            />
          )}
        </div>

        {/* Liste des mouvements */}
        <div 
          ref={listWrapRef} 
          className={classes.listColumnWrap} 
          style={listDynamicHeight ? { height: listDynamicHeight } : undefined}
        >
          <div className={classes.listScrollable}>
            {selectedMetal && paginatedMetalData.length > 0 ? (
              <MovementsTable
                rows={paginatedMetalData}
                tab={tab}
                selectedMetal={selectedMetal}
                metalGroups={progressiveByMetal.displayGroups}
                metauxList={metauxList}
                onMetalChange={setSelectedMetal}
                onEdit={editFromAggregated}
                onDelete={handleDelete}
                onPrint={handlePrint}
                onPrintPoliceBook={handlePrintPoliceBook}
                editRowId={editRow?.id}
                editClalRowId={editClalRow?.id}
                lastEditedRowId={lastEditedRowId}
                bodyScrollHeight={bodyScrollHeight}
                headerBlockRef={headerBlockRef}
                totalPrixAchat={totalPrixAchat}
              />
            ) : (
              <Box p={3} textAlign="center">
                Aucun mouvement pour les filtres sélectionnés
              </Box>
            )}
          </div>

          {tab === TAB_INDICES.CLAL && (
            <PaginationControls
              metrics={paginationMetrics}
              itemsPerPage={clalRowsPerPage}
              onPageChange={safeSetClalPage}
              onItemsPerPageChange={safeSetClalRowsPerPage}
            />
          )}
        </div>
      </div>

      <DeleteDialog
        open={!!deleteDialog}
        deleteType={deleteDialog?.type}
        onConfirm={confirmDeletion}
        onCancel={() => setDeleteDialog(null)}
      />
    </div>
  );
};

MouvementsOrList.propTypes = {
  rawMovementsData: PropTypes.object.isRequired,
  metauxList: PropTypes.array.isRequired,
  filtFournisseurs: PropTypes.array.isRequired,
  filtClients: PropTypes.array.isRequired,
  perteCoursClient: PropTypes.number,
  coursVieilOr: PropTypes.number,
  fetchMouvementsOr: PropTypes.func.isRequired,
  deleteMouvementOr: PropTypes.func.isRequired,
  createMouvementOr: PropTypes.func.isRequired,
  updateMouvementOr: PropTypes.func.isRequired,
  fetchMouvementsOrClient: PropTypes.func.isRequired,
  deleteMouvementOrClient: PropTypes.func.isRequired,
  createMouvementOrClient: PropTypes.func.isRequired,
  updateMouvementOrClient: PropTypes.func.isRequired,
  getListeFournisseur: PropTypes.func.isRequired,
  getMetaux: PropTypes.func.isRequired,
  getListeMetal: PropTypes.func.isRequired,
  getCours: PropTypes.func.isRequired,
  getListeClient: PropTypes.func.isRequired,
  fetchMouvementsClal: PropTypes.func.isRequired,
  createMouvementClal: PropTypes.func.isRequired,
  updateMouvementClal: PropTypes.func.isRequired,
  deleteMouvementClal: PropTypes.func.isRequired
};

const mapDispatchToProps = {
  fetchMouvementsOr,
  deleteMouvementOr,
  createMouvementOr,
  updateMouvementOr,
  getListeFournisseur,
  getCours,
  getListeClient,
  fetchMouvementsOrClient,
  createMouvementOrClient,
  updateMouvementOrClient,
  deleteMouvementOrClient,
  getMetaux,
  getListeMetal,
  fetchMouvementsClal,
  createMouvementClal,
  updateMouvementClal,
  deleteMouvementClal
};

export default connect(mapStateToPropsSelectors, mapDispatchToProps)(MouvementsOrList);
